import { useState } from "react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Marquee from "../components/Marquee";
import AutoScroll from "../components/AutoScroll";
import { Sig } from "../components/Brand";
import ReelForm from "../components/ReelModal";
import VideoTile from "../components/VideoTile";
import { IN_ACTION } from "../lib/media";

const BIO = [
  "Nitasha is a strategist in conscious luxury hospitality and a speaker on humanizing leadership, leverage and capacity.",
  "She researches and advises on how leaders, teams and guests live, lead and flourish when wellbeing evolves from an amenity into their most valuable asset.",
  "With 25 years building and advising luxury, clinical and corporate wellness, from the USA to the UAE, Miami to Monaco and Italy to India, her career began in the Fortune 500 at IBM and Procter & Gamble.",
  "She was Founding Director of Operations at an executive health and wellness centre in Toronto, then founded SKIN Medical Spa in 2006, where modern medicine met ancient Ayurvedic wisdom. She went on to found Global Life Group and Empowered360 educational experiences, created the wellness division proposal for Fairmont Monte-Carlo, led wellness for The Ritz-Carlton Toronto's Innovative Fitness team, and has been a guest professor in conscious leadership and luxury at the International University\u00a0of\u00a0Monaco.",
    "Across North America, Europe, the GCC and India, she has curated Villa Vitality™ experiences, Yacht Yoga™ and Mental Fitness Focus Training™ for executive and entrepreneurial leaders, and has spoken on stages around the world, from Mindvalley to Microsoft. Her latest vision and venture, which she founded and leads today, is the House of LUXEWELL. LUXEWELL's vision is to be the global changemaker in luxury wellness hospitality. Her mantra remains the same: Balanced Wellbeing is the Ultimate Luxury, because human flourishing fuels humanity flourishing.",
];
const STATS = [
  { n: "70+", l: "Countries Traveled" },
  { n: "25+", l: "Years on Stage and in Boardrooms" },
  { n: "9", l: "Countries Spoken In" },
  { n: "7", l: "Countries Lived and Led" },
];
const STAGES = ["Mindvalley — Educational Tech|Tallinn · Toronto · Los Angeles · New Delhi · Milan", "DOKU.TECH / OctoberTech|Prishtina", "Microsoft|USA", "Vulcan Inc.|Saint-Jean-Cap-Ferrat · Venice · London", "IBM|Toronto", "Procter & Gamble|Toronto", "International University of Monaco|Monaco", "Club des Leaders|Monaco · Geneva", "CBC Glenn Gould Studio|Toronto", "Robin Sharma International Leadership Team|Toronto · Global", "Entrepreneur Resorts|Bali", "Banasthali Vidyapith|Jaipur", "Evolve Wellness|New Delhi", "Govardhan Ecovillage|Mumbai", "Indigo|Toronto"];
const HOSTED = ["Thirty Nine Monte-Carlo", "Fairmont Monte-Carlo", "Bal de la Rose, Monaco", "Hôtel du Cap-Eden-Roc", "Hôtel Barrière Le Majestic, Cannes Film Festival", "A private villa, Saint-Jean-Cap-Ferrat", "The Ritz-Carlton Toronto", "Hotel X Toronto", "Delano South Beach, Miami", "Mondrian South Beach, Miami", "Faena Hotel, Miami Beach", "Hotel Mediterraneo, Sorrento", "Art Hotel Villa Fiorella, Massa Lubrense", "Villa Treville, Positano", "Komune, Bali"];
const scrollToBook = () => document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });

function NameList({ title, heading, items, testid }) {
  return (
    <div data-testid={testid}>
      <p className="eyebrow !text-gold mb-10">{title}</p>
      {heading && <h2 className="mb-6 mt-4"><span className="font-sign lowercase text-gold text-5xl mr-2">the</span><span className="font-display text-3xl tracking-tight">{heading}</span></h2>}
      <ul className="sm:columns-2 lg:columns-3 gap-10">
        {items.map((s) => { const [n, sub] = s.split("|"); return (
          <li key={s} className="break-inside-avoid font-headline text-lg text-ink/80 leading-snug py-2 border-b border-gold/20">{n}{sub && <span className="block text-sm text-ink/55">{sub}</span>}</li>
        ); })}
      </ul>
    </div>
  );
}
const SEEN = ["MINDVALLEY", "L'OFFICIEL", "DOKU.TECH", "39 MONTE-CARLO", "INTERNATIONAL UNIVERSITY OF MONACO", "DEUTSCHE BANK", "MICROSOFT", "ACCENTURE", "VULCAN", "THE RITZ-CARLTON", "FAIRMONT HOTELS & RESORTS", "RBC", "NYU"];

const STRIP = ["11-doku-tech-storytelling-mandela", "13-monaco-empowered360-teaching", "16-mindvalley-india-audience", "09-mindvalley-vishen", "03-global-changemakers", "06-mindvalley-flipchart", "07-indigo-toronto", "12-doku-tech-global-changemakers", "14-monaco-empowered360-laughing", "15-mindvalley-india-brule-test", "17-mindvalley-india-in-the-room", "26-bali-flow-fest-audience", "27-bali-flow-fest-seated", "05-monaco-empowered360", "08-monaco-boardroom", "IUM_2231_lola", "IUM_monaco_classroom_lola"].map((f) => `/speakers/${f}.jpg`);

const COLS = [
  { h: "Hospitality & luxury", talks: [
    ["The Final Currency of Luxury Hospitality", "Why leverage wellness, enhanced capacity and humanized hospitality win the next decade."],
    ["From Amenity to Asset", "wellness as a real ROI revenue division, far beyond spa, fitness and yoga"],
    ["Conscious Luxury: Beyond Optimization", "the art of balancing inner luxury — wealth, wellness and wisdom within — with outer luxury — leadership, lifestyle and legacy"],
  ] },
  { h: "Clinical & corporate wellness", talks: [
    ["Leverage Wellness™", "optimizing our most precious resources — time, energy, attention and money — to enhance capacity and humanize leadership"],
    ["Beyond the Treatment Room", "modern science, ancient wisdom and the patient journey"],
    ["The Luxury Wellness Legacy", <>human flourishing fuels humanity flourishing, <span className="gold-deep font-headline italic text-base">from the human to the hive to humanity</span></>],
  ] },
  { h: "Leadership & tech", talks: [
    ["Catalysts of Change", "immersive culture and connection experiences are catalysts for creating conscious leaders, who create rippling effects of change"],
    ["Strategic Chameleon™", "Mastering the art of adaptable change: keeping your vision, values and virtues intact internally, while adapting to the fluidity of change and powerful pivots externally."],
    ["The Beautiful Burnout", "Redefining the SADS of success: Stress, Anxiety, Depression and Stagnation."],
  ] },
];

export default function Speakers() {
  const [more, setMore] = useState(false);
  return (
    <div className="min-h-screen bg-ivory text-ink">
      <Header variant="light" active="speakers" />

      <section className="relative h-[88vh] min-h-[600px] w-full overflow-hidden" data-testid="speakers-hero">
        <div aria-hidden="true" style={{ backgroundImage: "url(/speakers/speakers-hero-toronto-spotlight.jpg)", filter: "blur(28px) brightness(0.55) saturate(0.8)" }}
          className="hidden lg:block absolute -inset-10 bg-cover bg-center" />
        <div role="img" aria-label="Nitasha Sarin speaking at the Toronto Speaker Circle" data-testid="speakers-hero-photo" style={{ backgroundImage: "url(/speakers/speakers-hero-toronto-spotlight.jpg)", filter: "sepia(0.06) saturate(0.88) contrast(0.98) brightness(1.02)" }}
          className="absolute inset-0 h-full w-full bg-cover lg:bg-contain bg-no-repeat bg-[position:65%_center] lg:bg-[position:right_center]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 via-ink/20 to-transparent" />
        <div className="on-water absolute bottom-0 inset-x-0">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 lg:pb-16">
            <p className="eyebrow gold-text mb-8" data-testid="speaking-eyebrow">Speaking</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ivory" data-testid="speakers-heading"><span className="font-sign gold-text lowercase tracking-normal mr-3">the</span>NEXT ERA OF WELLNESS<br />& HUMAN FLOURISHING</h1>
          </div>
        </div>
      </section>


      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-14" data-testid="about-nitasha">
        <p className="eyebrow !text-[0.62rem] !text-gold mb-10 leading-relaxed" data-testid="speaking-formats">Keynotes · Panels · Moderation · Workshops · Masterclasses</p>
        <div className="grid lg:grid-cols-2 gap-14 items-start lg:items-stretch">
        <div>
          <h2 className="leading-[1.03] whitespace-nowrap" data-testid="nitasha-name"><span className="font-sign lowercase text-gold text-4xl sm:text-5xl lg:text-6xl">nitasha sarin</span> <span className="font-display text-gold text-base sm:text-lg lg:text-xl tracking-normal align-baseline">MSc, MBA</span></h2>
          <p className="keep-italic mt-6 font-headline italic text-2xl text-ink/75" data-testid="nitasha-subheading">Founder & Chief Experience Officer, House of LUXEWELL</p>
          <div className="mt-8 space-y-4 max-w-xl" data-testid="nitasha-bio">
            {BIO.slice(0, 4).map((p) => <p key={p.slice(0, 20)} className="text-sm text-ink/75 leading-relaxed"><Sig>{p}</Sig></p>)}
            {more && <div className="space-y-4" data-testid="nitasha-bio-more">{BIO.slice(4).map((p) => <p key={p.slice(0, 20)} className="text-sm text-ink/75 leading-relaxed"><Sig>{p}</Sig></p>)}</div>}
            <button onClick={() => setMore((v) => !v)} data-testid="bio-readmore" className="eyebrow !text-[0.62rem] !text-ink border-b border-gold pb-0.5 hover:!text-gold transition-colors">{more ? "Read less" : "Read more"}</button>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
          className="w-full flex flex-col lg:justify-end lg:pb-[2.4rem]">
          <div className="relative p-3">
            <div className="absolute inset-0 border border-gold/60" />
            <div className="relative aspect-[1435/1096] overflow-hidden" data-testid="about-nitasha-photo">
              <img src="/speakers/mindvalley-tallinn-green.jpg" alt="Nitasha Sarin keynote, Mindvalley University, Tallinn 2018" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
          <div className="pt-3" data-testid="bio-photo-caption">
            <p className="eyebrow !text-[0.58rem] !text-gold">Mindvalley University · Tallinn, Estonia · 2018</p>
            <p className="font-sans text-xs text-ink/70 mt-1">Keynote Speaker: Code of the Extraordinary Mind | Be Extraordinary Seminar</p>
          </div>
        </motion.div>
        </div>
      </section>

      <section className="pt-2 pb-8" data-testid="photo-strip">
        <AutoScroll speed={0.35}>
          {STRIP.map((s, i) => (
            <div key={i} className="gold-frame stage-photo shrink-0 mr-3" data-testid={`strip-photo-${i}`}>
              <img src={s} alt={s.includes("IUM_") ? "Nitasha Sarin teaching conscious leadership, International University of Monaco" : "Nitasha Sarin on stage"} loading="lazy" className="block h-[240px] lg:h-[320px] w-auto max-w-none object-cover" />
            </div>
          ))}
        </AutoScroll>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12 space-y-12" data-testid="stages-lists">
        <NameList title="Stages & Partners" items={STAGES} testid="stages-partners" />
        <NameList title="Events & Experiences · Where We've Hosted & Spoken" items={HOSTED} testid="events-hosted" />
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-10" data-testid="reel-block">
        <div className="border border-gold/40 bg-bone grid md:grid-cols-[260px_1fr] items-stretch">
          <div className="stage-photo min-h-[300px]"><img src="/photos/17-nitasha-rock-portrait.jpg" alt="Nitasha Sarin portrait" className="absolute inset-0 w-full h-full object-cover" /></div>
          <div className="p-8 lg:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="eyebrow mb-3 !text-gold">On Stage</p>
              <p className="font-headline text-2xl lg:text-3xl leading-snug max-w-xl">A full-length speaker reel is shared with event organizers on request.</p>
            </div>
            <button data-testid="request-reel-btn" onClick={scrollToBook} className="btn-lux w-fit shrink-0 bg-ink text-ivory hover:bg-gold hover:text-ink">Request access</button>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12" data-testid="nitasha-in-action">
        <p className="eyebrow !text-gold mb-8" data-testid="in-action-eyebrow">Seminars & Workshops</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight" data-testid="in-action-heading"><span className="font-sign lowercase text-gold tracking-normal text-5xl mr-2">nitasha's</span>Perspective & Praise</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {IN_ACTION.videos.map((v, i) => (
            <div key={v.youtubeId} className="frame-out"><div className="relative">
              <VideoTile v={v} testid={`in-action-video-${i + 1}`} cover={
                <div className="absolute inset-0 bg-[#1f2a22]/[0.68] flex flex-col items-center justify-center text-center text-ivory px-6">
                  <p className="eyebrow !text-[0.55rem] !text-gold">The Balanced Business Method™ · Part {v.part}</p>
                  <p className="font-headline italic text-4xl lg:text-5xl mt-3">Conscious Luxury</p>
                  <p className="text-sm mt-2 text-ivory/85">where inner luxury meets outer leadership</p>
                  <p className="text-xs mt-3 text-ivory/70">Monte-Carlo, Monaco</p>
                </div>} />
            </div></div>
          ))}
        </div>
        <p className="mt-5 text-xs text-ink/50" data-testid="in-action-note">{IN_ACTION.note}</p>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-6 grid grid-cols-2 lg:grid-cols-4 gap-px bg-gold/25 border border-gold/25">
        {STATS.map((s) => (
          <div key={s.l} className="bg-ivory p-7">
            <p className="font-display text-5xl text-gold">{s.n}</p>
            <p className="eyebrow !text-[0.55rem] !text-ink/55 mt-3 leading-relaxed">{s.l}</p>
          </div>
        ))}
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-14">
        <p className="eyebrow mb-4">Credentials</p>
        <div className="space-y-1.5" data-testid="credentials-line">{["MBA, Innovation Leadership & Luxury Management (Monaco)", "MSc, Science of Pedagogy & Education (USA)", "BA, Organizational Behaviour & Cognitive Psychology (Canada)"].map((c) => <p key={c} className="eyebrow !text-[0.62rem] !text-ink/75">{c}</p>)}</div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-14 border-t border-gold/25 pt-12">
        <p className="eyebrow mb-6">As Seen On</p>
        <Marquee duration={60} testid="seen-ticker">
          {SEEN.map((s) => (
            <span key={s} className="flex items-center gap-6 pr-6 shrink-0">
              <span className="eyebrow !text-[0.62rem] !text-ink/65 whitespace-nowrap">{s}</span>
              <span className="text-gold/50">·</span>
            </span>
          ))}
        </Marquee>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-12 border-t border-gold/25 pt-12">
        <div className="border border-gold/40 bg-bone p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center" data-testid="featured-talk">
          <div>
            <p className="eyebrow mb-4 !text-gold">Featured Keynote</p>
            <h2 className="font-headline text-2xl lg:text-3xl leading-tight max-w-3xl">Humanizing Tech: Balancing EI & AI — Oxymoron, Obligation or Opportunity?</h2>
            <p className="mt-4 text-sm text-ink/55">Headline keynote · DOKU.TECH · Prishtina · 2024</p>
            <button onClick={scrollToBook} data-testid="speakers-book-cta" className="btn-lux mt-8 w-fit bg-ink text-ivory hover:bg-gold hover:text-ink">Book Nitasha to speak</button>
          </div>
          <div className="gold-frame stage-photo aspect-[16/10]" data-testid="featured-talk-photo">
            <img src="/speakers/02-doku-tech-2024.jpg" alt="Nitasha Sarin keynote, Humanizing Tech, DOKU.TECH 2024" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-10" data-testid="speaking-topics">
        <p className="eyebrow mb-2">Speaking Topics</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-8"><span className="font-sign lowercase text-gold tracking-normal text-5xl mr-2">signature</span>Keynotes</h2>
        <div className="grid md:grid-cols-3 gap-px bg-gold/25 border border-gold/25">
          {COLS.map((c) => (
            <div key={c.h} className="bg-ivory p-7 lg:p-8" data-testid={`talks-${c.h.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
              <p className="eyebrow mb-5">{c.h}</p>
              <ul className="space-y-4">
                {c.talks.map(([t, s, it]) => (
                  <li key={t} className="border-b border-gold/20 pb-4" data-testid={`talk-${t.slice(0, 16)}`}>
                    <p className={`font-headline font-semibold text-lg text-ink leading-snug lg:whitespace-nowrap ${it ? "italic" : ""}`}>{t}</p>
                    <p className="text-sm text-ink/60 mt-1 leading-relaxed">{s}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-5 text-sm text-ink/55 italic" data-testid="topics-note">Bespoke keynotes & customized seminars, tailored to your audience and event — enquire within.</p>
      </section>

      <section id="book" className="px-6 lg:px-10 max-w-7xl mx-auto pb-24" data-testid="book-section">
        <div className="border border-gold/40 bg-bone p-8 lg:p-12">
          <p className="text-4xl text-gold mb-4"><span className="font-headline italic text-[0.62em] mr-2">the</span><span className="font-sign">stage</span></p>
          <h2 className="font-display text-2xl lg:text-3xl tracking-tight mb-8" data-testid="book-heading">BOOK NITASHA OR REQUEST THE SPEAKER REEL</h2>
          <ReelForm />
        </div>
      </section>
    </div>
  );
}
